import { useRef, useEffect, useState } from 'react';

import useTrackSelectParentSize from './use-track-select-parent-size';
import createScheduler from '@/polyfills/create-scheduler';
import globalWindowSize from '@/utils/global-window-size';

const useSetMenuPosition = ({
  controlRef,
  menuRef,
  pause,
  recalculate,
  dynamicMenuPlacement,
}: {
  pause: boolean;
  controlRef: React.RefObject<HTMLElement | null>;
  menuRef: React.RefObject<HTMLElement | null>;
  recalculate?: number;
  dynamicMenuPlacement?: boolean;
}) => {
  const selectRefParentRef = useRef<HTMLElement | null>(null);
  const hasTracked = useRef(false);

  const [optimisticallyCalculated, setOptimisticallyCalculated] = useState(false);

  useEffect(() => {
    selectRefParentRef.current = controlRef.current?.parentElement || null;
  }, [controlRef]);

  useEffect(() => {
    if (!selectRefParentRef.current) return;

    if (hasTracked.current) {
      setOptimisticallyCalculated(false); // reset to false to allow pause to work as expected
      return;
    }

    // optimistic calculation to prevent initial brief flash first time we open the menu due to fixed positioning
    // starting at top: 0 left: 0
    const taskScheduler = createScheduler();
    taskScheduler.schedule(
      () => {
        setOptimisticallyCalculated(true);
        hasTracked.current = true;
      },
      {
        delay: 0,
        priority: 'background',
      }
    );

    return () => {
      taskScheduler.yield();
    };
  }, []);

  // Track menu height via ResizeObserver so the positioning effect re-runs as soon as
  // the menu gains height. Reading offsetHeight inline is unreliable on repeated opens
  // because the DOM may not have painted yet, yielding 0 and breaking dynamic placement.
  const [menuHeight, setMenuHeight] = useState(0);
  useEffect(() => {
    const el = menuRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => setMenuHeight(el.offsetHeight));
    observer.observe(el);
    return () => observer.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const boundingRect = useTrackSelectParentSize({
    ref: selectRefParentRef,
    pause: (pause && !optimisticallyCalculated) || !menuHeight,
    recalculate,
  });

  useEffect(() => {
    if (pause && !optimisticallyCalculated) return;
    if (!menuRef.current) return;
    if (!selectRefParentRef.current) return;

    // When dynamic placement is active, defer until the menu has a measurable height.
    // The ResizeObserver above will trigger this effect again once height is non-zero.
    if (dynamicMenuPlacement && menuHeight === 0) return;

    const viewportHeight = globalWindowSize.innerHeight;

    const presentationEl = controlRef.current?.querySelector('label[role="presentation"]');
    const presentationElRect = presentationEl?.getBoundingClientRect();
    // first try using the presentation label because that's the control without the header
    const topPos = presentationElRect?.top || boundingRect.top;

    // Space available below and above the control
    const spaceBelow = viewportHeight - boundingRect.bottom;
    const spaceAbove = topPos;

    let topPosition: number;
    const gap = 4;

    if (
      dynamicMenuPlacement &&
      menuHeight > spaceBelow && // if menu height is greater than available space below
      menuHeight < spaceAbove && // if menu height is less than available space above (this is to always go below in cases where there's not enough space either way)
      spaceAbove > spaceBelow // if available space above is greater than available space below
    ) {
      // Position above: control top minus menu height minus gap
      topPosition = topPos - menuHeight - gap;
    } else {
      // Position below (default): control bottom plus gap
      topPosition = boundingRect.bottom + gap;
    }

    menuRef.current.style.setProperty('--select-menu-top', `${topPosition}px`);
    menuRef.current.style.setProperty('--select-menu-left', `${boundingRect.left}px`);
    menuRef.current.style.setProperty('--select-menu-width', `${boundingRect.width}px`);
  }, [
    boundingRect,
    pause,
    menuRef,
    optimisticallyCalculated,
    recalculate,
    dynamicMenuPlacement,
    menuHeight,
    controlRef,
  ]);
};

export default useSetMenuPosition;
